import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExtaLifeService } from 'lavva.exalushome.extalife';
import { NetworkConfigurationService } from 'lavva.exalushome.network';
import { PortosService } from 'lavva.exalushome.portos';
import { WebCamsService } from 'lavva.exalushome.webcams';
import { WektaIntercomsService } from 'lavva.exalushome.wektaintercoms';
import { SetGeolocationResponseCode } from 'lavva.exalushome/build/js/Services/Controller/IGeolocationService';
import { ResponseResult } from 'lavva.exalushome/build/js/Services/FieldChangeResult';
import { orderBy } from 'lodash';
import * as uuid from 'uuid';
import { useMutation } from '@apollo/client';
import { useBackdropContext } from '../../../context/backdrop';
import { useInstallationContext } from '../../../context/installation';
import {
  CreateInstallationMutation,
  CreateInstallationMutationVariables,
  IntegrationType,
  UserInstallation,
  ExalusInstallationPayload,
} from '../../../data-access/gql-types/graphql';
import { CREATE_INSTALLATION } from '../../../data-access/mutations/installations';
import { environment } from '../../../environment';
import { useExalusContext } from '../../../integrations/exalus/context';
import { useExalusSetGeolocation, useHandleGeolocationError } from '../../../integrations/exalus/hooks/geolocation';
import { useExalusLocalStorage } from '../../../integrations/exalus/hooks/storage';
import { ExalusIntegrationForm } from '../../../integrations/exalus/modules/integration-create/types';
import { LoginForm } from '../../../integrations/exalus/modules/integration-login/types';
import { ExalusInstallationCredentials } from '../../../integrations/exalus/types/exalus';
import { saveExalusCredentials } from '../../../integrations/exalus/utils';
import { useGetBrokerToCreateInstallation } from '../../../integrations/lavva/api/installation/installation.hooks';
import * as storage from '../../../integrations/lavva/storage';
import { ROUTES } from '../../../routes/routes';
import { useUserProfileStore } from '../../../store/user-profile';
import { IntegrationDefaultEnum } from '../../../types';
import { integrationTypeByIntegrationDefault } from '../../../utils/helpers/integrations';
import { isLocalApp } from '../../../utils/helpers/local-app';
import { toastError, toastSuccess } from '../../../utils/toast';
import { StateCreateInstallationType, initialState, useCreateInstallationContext } from '../context';
import {
  RESET_INSTALLATION_CREATE,
  SET_CREATE_LOADING,
  SET_EXALUS_CREDENTIALS,
  SET_HEX_COLOR,
  SET_ICON_NAME,
  SET_INSTALLATION_NAME,
  SET_INTEGRATION_TYPE,
  SET_LOCATION_LAT,
  SET_LOCATION_LONG,
  SET_TIME_ZONE,
} from '../context/constants';
import { CreateInstallationControlForm } from '../types';

export const useCreateInstallation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('installation');
  const { t: tc } = useTranslation('common');
  const { installationList, updateInstallationList, selectInstallation, setSelectedInstallationId } =
    useInstallationContext();
  const refetchUserMe = useUserProfileStore((s) => s.refetchUserMe);
  const [createInstallation] = useMutation<CreateInstallationMutation, CreateInstallationMutationVariables>(
    CREATE_INSTALLATION,
  );
  const { dispatch } = useCreateInstallationContext();
  const { handleGeolocationError } = useHandleGeolocationError();
  const { setConnectionExists, setInited } = useExalusContext();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { mutate: changeControllerGeolocation } = useExalusSetGeolocation();
  const { getBroker } = useGetBrokerToCreateInstallation();
  const { saveExalusDataStorage } = useExalusLocalStorage();

  const nextStep = (installationState: StateCreateInstallationType) => {
    switch (environment.INTEGRATION_DEFAULT) {
      case IntegrationDefaultEnum.Exalus: {
        navigate(ROUTES.ExalusIntegrationCreate(installationState));
        break;
      }
      case IntegrationDefaultEnum.Wisniowski: {
        submitInstallation(installationState);
        break;
      }
      default: {
        navigate(ROUTES.IntegrationsList());
        break;
      }
    }
  };

  const handleSaveCreateInstallationForm = (form: CreateInstallationControlForm) => {
    dispatch({ type: SET_INSTALLATION_NAME, payload: form.installation_name });
    dispatch({ type: SET_HEX_COLOR, payload: form.hex_color });
    dispatch({ type: SET_ICON_NAME, payload: form.icon_name });
    dispatch({ type: SET_TIME_ZONE, payload: form.time_zone });
    dispatch({ type: SET_INTEGRATION_TYPE, payload: integrationTypeByIntegrationDefault() });

    if (form.location) {
      const locationParts = form.location.trim().split(',');
      dispatch({ type: SET_LOCATION_LAT, payload: parseFloat(locationParts[0]) });
      dispatch({ type: SET_LOCATION_LONG, payload: parseFloat(locationParts[1]) });
    }

    nextStep({
      ...initialState,
      installationName: form.installation_name,
      iconName: form.icon_name,
      hexColor: form.hex_color,
      timeZone: form.time_zone,
      integrationType: integrationTypeByIntegrationDefault(),
      locationLat: form.location ? parseFloat(form.location.trim().split(',')[0]) : null,
      locationLong: form.location ? parseFloat(form.location.trim().split(',')[1]) : null,
    });
  };

  const setSubmitLoading = (value: boolean) => {
    dispatch({ type: SET_CREATE_LOADING, payload: value });
  };

  const editIntegration = (integrationtype: IntegrationType, exalusCredentials?: ExalusIntegrationForm) => {
    dispatch({
      type: SET_INTEGRATION_TYPE,
      payload: integrationtype,
    });

    if (exalusCredentials) {
      dispatch({
        type: SET_EXALUS_CREDENTIALS,
        payload: {
          serialNumber: exalusCredentials.serialNumber,
          pin: exalusCredentials.pin,
        },
      });
    }
  };

  const resetInstallationCreate = () => {
    dispatch({ type: RESET_INSTALLATION_CREATE });
  };

  const finishExalusInstallation = (
    installationData: UserInstallation,
    newInstallationList: UserInstallation[],
    formData: LoginForm,
  ) => {
    setConnectionExists(true);

    const credentials: ExalusInstallationCredentials = {
      ...formData,
      id: installationData.installationId,
      serialNumber: (installationData.externalPayload as ExalusInstallationPayload).serialNumber,
      pin: (installationData.externalPayload as ExalusInstallationPayload).pin,
    };

    saveExalusCredentials(credentials);

    if (!isLocalApp) {
      saveExalusDataStorage('iconName', installationData.iconName);
      saveExalusDataStorage('hexColor', installationData.hexColor);
      saveExalusDataStorage('name', installationData.name);
    }

    ExtaLifeService.Init();
    NetworkConfigurationService.Init();
    PortosService.Init();
    WebCamsService.Init();
    WektaIntercomsService.Init();
    setInited(true);
    turnOffBackdrop();

    if (isLocalApp) {
      storage.setLocalItem('installationList', newInstallationList);
      setSelectedInstallationId(installationData.installationId);
      navigate(ROUTES.Root());
      return;
    }

    window.location.href = ROUTES.InstallationCreated();
  };

  const finishCreatingInstallation = (installationData: UserInstallation, formData?: LoginForm) => {
    const newInstallationList = Array.from(installationList);
    let installationIndex = newInstallationList.findIndex(
      ({ installationId }) => installationId === installationData.installationId,
    );
    let installationOrder = 0;

    if (installationIndex < 0) installationIndex = newInstallationList.length;
    if (installationList.length) {
      installationOrder = orderBy(newInstallationList, 'order')[newInstallationList.length - 1].order + 1;
    }

    newInstallationList[installationIndex] = { ...installationData, order: installationOrder };
    updateInstallationList(newInstallationList);
    refetchUserMe();

    if (!isLocalApp) {
      toastSuccess({ content: t('installationCreateSuccess') });

      selectInstallation(installationData.installationId, () => {
        if (
          installationData.integrationType === IntegrationType.Exalus &&
          (installationData.externalPayload as ExalusInstallationPayload).installationId &&
          formData
        ) {
          finishExalusInstallation(installationData, newInstallationList, formData);
        } else {
          resetInstallationCreate();
          turnOffBackdrop();
          navigate(ROUTES.InstallationCreated());
        }
      });
    } else {
      if (
        installationData.integrationType === IntegrationType.Exalus &&
        (installationData.externalPayload as ExalusInstallationPayload).installationId &&
        formData
      ) {
        finishExalusInstallation(installationData, newInstallationList, formData);
      }
    }
  };

  const submitInstallation = async (installationState: StateCreateInstallationType, formData?: LoginForm) => {
    turnOnBackdrop();
    setSubmitLoading(true);
    await getBroker();

    createInstallation({
      variables: {
        input: {
          installationId: uuid.v4(),
          iconName: installationState.iconName,
          hexColor: installationState.hexColor,
          name: installationState.installationName,
          timeZone: installationState.timeZone,
          integrationType: installationState.integrationType,
          externalInstallationPayload: {
            exalusInstallationPayload:
              installationState.exalusCredentials.serialNumber && installationState.exalusCredentials.pin
                ? {
                    serialNumber: installationState.exalusCredentials.serialNumber,
                    pin: installationState.exalusCredentials.pin,
                  }
                : null,
          },
          ...(installationState.locationLat && installationState.locationLong
            ? {
                coordinates: {
                  latitude: installationState.locationLat,
                  longitude: installationState.locationLong,
                },
              }
            : {}),
        },
      },
      onCompleted: (data) => {
        if (data?.createInstallation) {
          if (
            installationState.integrationType === IntegrationType.Exalus &&
            installationState.locationLat &&
            installationState.locationLong
          ) {
            changeControllerGeolocation(
              { Latitude: installationState.locationLat, Longitude: installationState.locationLong },
              {
                onSuccess: (result: ResponseResult<SetGeolocationResponseCode>) => {
                  if (result.Type === SetGeolocationResponseCode.OK) {
                    finishCreatingInstallation(data.createInstallation as UserInstallation, formData);
                  } else {
                    handleGeolocationError(result.Type);
                  }
                },
              },
            );
          } else {
            finishCreatingInstallation(data.createInstallation as UserInstallation, formData);
          }
        } else {
          toastError({ content: tc('errors.somethingWentWrong') });
          // TODO errors
          turnOffBackdrop();
        }

        setSubmitLoading(false);
      },
      onError: () => {
        turnOffBackdrop();
        setSubmitLoading(false);
      },
    });
  };

  return {
    handleSaveCreateInstallationForm,
    resetInstallationCreate,
    editIntegration,
    submitInstallation,
    setSubmitLoading,
    finishCreatingInstallation,
  };
};
